import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { fetchAuctionTeamSetup, fetchTournamentById } from '../../../services/tournamentService';
import { getCommunityId } from '../../../utils/community';
import { getClubId } from '../../../utils/club';
import { extractMatchSchedulePayload, fetchMatchSchedule, saveMatchSchedule, scheduleMatches } from '../../../services/matchService';
import { FeatureBadgeSlot } from '../../commercial/FeatureExperience';
import { getMatchStartResumePath, getStartResumeLabel } from '../../../utils/matchStatus';
import { userHasRole } from '../../../utils/userRoles';
import { normalizeId } from '../../../utils/ids';
import { buildMatchManagerMatchesPath, buildMatchManagerMatchTossPath, buildMatchManagerTournamentPath } from '../../../utils/matchManagerPaths';

const defaultDates = ['30th Aug', '31st Aug'].join('\n');
const defaultTimes = ['7:15am', '9:15am', '11am', '1pm', '2:30pm'].join('\n');

const parseList = (value) =>
  value
    .split(/[\n,]+/)
    .map((item) => item.trim())
    .filter(Boolean);

const FORMAT_OPTIONS = [
  { value: 'round-robin', label: 'Round Robin' },
  { value: 'knockout', label: 'Knockout' },
  { value: 'league_final', label: 'League + Final' },
  { value: 'league_semifinal_final', label: 'League + Semifinal + Final' },
  { value: 'ipl_playoffs', label: 'IPL-style Playoffs' },
  { value: 'group_knockout', label: 'Group Stage + Knockout' },
  { value: 'double_elimination', label: 'Double Elimination' },
];

const normalizeFormat = (value) => FORMAT_OPTIONS.some((option) => option.value === value) ? value : 'round-robin';

// ✅ Custom breadcrumbs example:
// const crumbs = [
//   { label: 'Home', to: '/' },
//   { label: 'Community', to: `/community/${communityId}` },
//   { label: 'Match Manager', to: `/community/${communityId}/match-manager/${managerId}` },
//   { label: 'Manual Match Format' },
// ];
// <PageShell breadcrumbs={crumbs}>...</PageShell>
// <PageShell breadcrumbOverrides={{ tournamentId: tournament?.name || 'Tournament' }}>
const MatchManagementPage = () => {
  const {
    organizationId,
    communityId: routeCommunityId,
    clubId: routeClubId,
    individualTournamentId,
    tournamentId,
    managerId: routeManagerId,
  } = useParams();
  const { token, user } = useAuth();
  const [searchParams] = useSearchParams();
  const managerId = normalizeId(routeManagerId) || normalizeId(user);
  const communityId = useMemo(() => getCommunityId(routeCommunityId), [routeCommunityId]);
  const clubId = useMemo(() => getClubId(routeClubId), [routeClubId]);
  const [tournament, setTournament] = useState(null);
  const [tournamentError, setTournamentError] = useState('');
  const [isLoadingTournament, setIsLoadingTournament] = useState(false);

  const [teamsInput, setTeamsInput] = useState('');
  const [datesInput, setDatesInput] = useState(defaultDates);
  const [timesInput, setTimesInput] = useState(defaultTimes);
  const [protectedTimesInput, setProtectedTimesInput] = useState('7:15am');
  const [maxMatchesPerBlock, setMaxMatchesPerBlock] = useState(2);
  const [scheduleOutput, setScheduleOutput] = useState([]);
  const [scheduleMeta, setScheduleMeta] = useState(null);
  const [selectedFormat, setSelectedFormat] = useState(normalizeFormat(searchParams.get('format')));
  const [formatConfig, setFormatConfig] = useState({
    seeding: 'random',
    groupCount: 2,
    qualifiersPerGroup: 2,
  });
  const [scheduleError, setScheduleError] = useState('');
  const [isScheduling, setIsScheduling] = useState(false);
  const [isSavingSchedule, setIsSavingSchedule] = useState(false);
  const hasLoadedTeamsRef = useRef(false);
  const userTeamRoles = useMemo(() => new Set([...(user?.teamRoles || [])].filter(Boolean)), [user]);
  const isIndividualAccount = userHasRole(user, 'individual') || user?.accountType === 'individual';
  const isMatchManager = userHasRole(user, 'match_manager');
  const isScorerRole = userHasRole(user, 'scorer') || userTeamRoles.has('scorer');
  const hasManageMatchesPermission =
    user?.canManageMatches === true ||
    user?.permissions?.canManageMatches === true ||
    user?.permissions?.match?.canManageMatches === true;
  const canManageSchedule =
    hasManageMatchesPermission ||
    isMatchManager ||
    userHasRole(user, 'tournament_admin') ||
    userHasRole(user, 'organization_admin') ||
    userHasRole(user, 'community_admin') ||
    userHasRole(user, 'club_admin') ||
    userHasRole(user, 'individual_admin') ||
    isIndividualAccount ||
    userHasRole(user, 'super_admin');
  const canConfigureMatch = canManageSchedule;
  const isScorerOnly = isScorerRole && !canManageSchedule;

  useEffect(() => {
    const loadTournament = async () => {
      if (!token || !tournamentId) {
        setTournament(null);
        setTournamentError('');
        return;
      }

      setIsLoadingTournament(true);
      setTournamentError('');

      try {
        const [tournamentResult, auctionTeamsResult] = await Promise.allSettled([
          fetchTournamentById(tournamentId, token, organizationId, { communityId, clubId }),
          fetchAuctionTeamSetup(tournamentId, token),
        ]);
        if (tournamentResult.status === 'rejected') {
          throw tournamentResult.reason;
        }
        const response = tournamentResult.value;
        setTournament(response.tournament || null);

        const scheduleResponse = await fetchMatchSchedule({ token, tournamentId });
        const { schedule: savedSchedule, meta: savedMeta } = extractMatchSchedulePayload(scheduleResponse);
        setScheduleOutput(savedSchedule);
        setScheduleMeta(savedMeta);
        if (savedMeta?.format) {
          setSelectedFormat(normalizeFormat(savedMeta.format));
        }
        if (savedMeta?.formatConfig && typeof savedMeta.formatConfig === 'object') {
          setFormatConfig((prev) => ({ ...prev, ...savedMeta.formatConfig }));
        }
        if (auctionTeamsResult.status === 'fulfilled') {
          const auctionTeams = auctionTeamsResult.value?.setup?.teams || [];
          if (!hasLoadedTeamsRef.current) {
            const teamNames = auctionTeams
              .map((team) => (typeof team?.name === 'string' ? team.name.trim() : ''))
              .filter(Boolean)
              .join('\n');
            if (teamNames) {
              setTeamsInput(teamNames);
              hasLoadedTeamsRef.current = true;
            }
          }
        }
      } catch (error) {
        setTournament(null);
        setTournamentError(error.message || 'Unable to load tournament details.');
      } finally {
        setIsLoadingTournament(false);
      }
    };

    loadTournament();
  }, [clubId, communityId, organizationId, token, tournamentId]);

  useEffect(() => {
    setSelectedFormat(normalizeFormat(searchParams.get('format')));
  }, [searchParams]);

  const handleSchedule = async (event) => {
    event.preventDefault();
    setScheduleError('');

    const teams = parseList(teamsInput);
    const dates = parseList(datesInput);
    const times = parseList(timesInput);
    const protectedTimeLabels = parseList(protectedTimesInput);

    if (!token) {
      setScheduleError('Login required to schedule matches.');
      return;
    }

    const groupCount = Number(formatConfig.groupCount) || 0;
    const qualifiersPerGroup = Number(formatConfig.qualifiersPerGroup) || 0;
    if (selectedFormat === 'knockout' && teams.length < 2) {
      setScheduleError('Knockout requires at least 2 teams.');
      return;
    }
    if (selectedFormat === 'ipl_playoffs' && teams.length < 4) {
      setScheduleError('IPL-style playoffs require at least 4 teams.');
      return;
    }
    if (selectedFormat === 'league_semifinal_final' && teams.length < 4) {
      setScheduleError('League + semifinal + final requires at least 4 teams.');
      return;
    }
    if (selectedFormat === 'group_knockout') {
      if (groupCount < 2) {
        setScheduleError('Group stage requires at least 2 groups.');
        return;
      }
      if (qualifiersPerGroup < 1) {
        setScheduleError('Group stage requires at least 1 qualifier per group.');
        return;
      }
      const maxPerGroup = Math.ceil(teams.length / groupCount);
      if (qualifiersPerGroup > maxPerGroup) {
        setScheduleError('Qualifiers per group cannot exceed the largest group size.');
        return;
      }
    }

    setIsScheduling(true);

    try {
      const response = await scheduleMatches({
        token,
        payload: {
          teams,
          dates,
          times,
          maxMatchesPerBlock: Number(maxMatchesPerBlock) || 1,
          protectedTimeLabels,
          tournamentId,
          format: selectedFormat,
          formatConfig,
        },
      });
      const { schedule: generatedSchedule, meta: generatedMeta } = extractMatchSchedulePayload(response);
      const nextMeta = generatedMeta ? { ...generatedMeta, format: generatedMeta.format || 'round-robin', formatConfig } : null;
      setScheduleOutput(generatedSchedule);
      setScheduleMeta(nextMeta);

    } catch (error) {
      setScheduleOutput([]);
      setScheduleMeta(null);
      setScheduleError(error.message || 'Unable to schedule matches.');
    } finally {
      setIsScheduling(false);
    }
  };

  const scheduleSummary = useMemo(() => {
    if (!scheduleMeta) return null;
    return `${scheduleMeta.totalMatches} matches across ${scheduleMeta.daysNeeded} days`;
  }, [scheduleMeta]);

  const scheduleSavedLabel = useMemo(() => {
    if (!scheduleMeta?.savedAt) return null;
    const savedDate = new Date(scheduleMeta.savedAt);
    if (Number.isNaN(savedDate.getTime())) return 'Schedule saved';
    return `Saved on ${savedDate.toLocaleString()}`;
  }, [scheduleMeta?.savedAt]);

  const handleSaveSchedule = async () => {
    if (!token || !tournamentId) {
      setScheduleError('Login required to save schedules.');
      return;
    }

    setIsSavingSchedule(true);
    setScheduleError('');

    try {
      const response = await saveMatchSchedule({
        token,
        tournamentId,
        payload: {
          schedule: scheduleOutput,
          meta: scheduleMeta
            ? { ...scheduleMeta, format: scheduleMeta.format || selectedFormat || 'round-robin', formatConfig }
            : { format: selectedFormat || 'round-robin', formatConfig },
        },
      });
      const { schedule: savedSchedule, meta: savedMeta } = extractMatchSchedulePayload(response);
      setScheduleOutput(savedSchedule);
      setScheduleMeta(savedMeta);
    } catch (error) {
      setScheduleError(error.message || 'Unable to save match schedule.');
    } finally {
      setIsSavingSchedule(false);
    }
  };

  const visibleSchedule = scheduleOutput;

  const matchManagerScopeParams = useMemo(() => ({
    organizationId,
    communityId,
    clubId,
    individualTournamentId,
    managerId,
    tournamentId,
  }), [clubId, communityId, individualTournamentId, managerId, organizationId, tournamentId]);

  const tournamentBasePath = useMemo(
    () => buildMatchManagerTournamentPath(matchManagerScopeParams),
    [matchManagerScopeParams]
  );

  const matchBasePath = useMemo(
    () => buildMatchManagerMatchesPath(matchManagerScopeParams),
    [matchManagerScopeParams]
  );

  const entityBase = useMemo(() => {
    if (communityId) return `/community/${communityId}`;
    if (clubId) return `/club/${clubId}`;
    if (organizationId) return `/org/${organizationId}`;
    if (individualTournamentId) return `/individual-tournament/${individualTournamentId}`;
    return '';
  }, [clubId, communityId, individualTournamentId, organizationId]);

  return (
    <div className="py-4">
      <div className="mb-4 d-flex flex-column gap-2">
        <p className="eyebrow text-uppercase text-primary fw-semibold mb-0">
          {canManageSchedule ? 'Match management' : 'Match scoring'}
        </p>
        <h1 className="h3 mb-1 d-flex align-items-center gap-2 flex-wrap">
          {canManageSchedule ? 'Build a match schedule' : 'Assigned matches'}
          {canManageSchedule ? <FeatureBadgeSlot featureKey="scheduler.advanced"/> : null}
        </h1>
        <p className="text-muted mb-0">
          {isScorerOnly
            ? 'Review the matches you have been assigned to score.'
            : 'Enter the participating teams, match days, and times to generate a round-robin schedule with per-day caps and protected time slots.'}
        </p>
        {!canManageSchedule && (
          <div className="alert alert-warning mb-0" role="status">
            Not authorized to schedule matches. You can only review assigned matches and scoreboards.
          </div>
        )}
        {entityBase && (
          <div className="d-flex flex-wrap gap-2">
            {!isScorerOnly && tournamentBasePath && (
              <Link
                className="btn btn-sm btn-outline-secondary"
                to={`${tournamentBasePath}/match-formats`}
              >
                Back to match formats
              </Link>
            )}
          </div>
        )}
      </div>

      {(tournamentError || scheduleError) && (
        <div className="alert alert-danger" role="alert">
          {tournamentError || scheduleError}
        </div>
      )}

      {isLoadingTournament && (
        <div className="alert alert-secondary" role="alert">
          Loading tournament details...
        </div>
      )}

      {tournament && (
        <div className="alert alert-info" role="alert">
          {isScorerOnly ? 'Scoring assignments for' : 'Scheduling for'}{' '}
          <strong>{tournament.name}</strong>
        </div>
      )}

      <div className="row g-4">
        {canManageSchedule && (
          <div className="col-12 col-lg-5">
            <form className="card shadow-sm border-0" onSubmit={handleSchedule}>
              <div className="card-body d-flex flex-column gap-3">
                <div>
                  <label className="form-label">Teams</label>
                  <textarea
                    className="form-control"
                    rows={6}
                    value={teamsInput}
                    onChange={(event) => setTeamsInput(event.target.value)}
                    placeholder="One team per line"
                  />
                </div>
                <div>
                  <label className="form-label">Fixture format</label>
                  <select className="form-select" value={selectedFormat} onChange={(event) => setSelectedFormat(event.target.value)}>
                    {FORMAT_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
                {selectedFormat === 'knockout' && (
                  <div>
                    <label className="form-label">Seeding</label>
                    <select
                      className="form-select"
                      value={formatConfig.seeding || 'random'}
                      onChange={(event) => setFormatConfig((prev) => ({ ...prev, seeding: event.target.value }))}
                    >
                      <option value="random">Random</option>
                      <option value="seeded">Seeded</option>
                    </select>
                  </div>
                )}
                {selectedFormat === 'group_knockout' && (
                  <div className="row g-3">
                    <div className="col-6">
                      <label className="form-label">Groups</label>
                      <input
                        type="number"
                        min="2"
                        className="form-control"
                        value={formatConfig.groupCount || 2}
                        onChange={(event) => setFormatConfig((prev) => ({ ...prev, groupCount: event.target.value }))}
                      />
                    </div>
                    <div className="col-6">
                      <label className="form-label">Qualifiers / group</label>
                      <input
                        type="number"
                        min="1"
                        className="form-control"
                        value={formatConfig.qualifiersPerGroup || 2}
                        onChange={(event) => setFormatConfig((prev) => ({ ...prev, qualifiersPerGroup: event.target.value }))}
                      />
                    </div>
                  </div>
                )}
                <div>
                  <label className="form-label">Match days</label>
                  <textarea
                    className="form-control"
                    rows={3}
                    value={datesInput}
                    onChange={(event) => setDatesInput(event.target.value)}
                    placeholder="Add each day label or date on a new line"
                  />
                </div>
                <div>
                  <label className="form-label">Match times per day</label>
                  <textarea
                    className="form-control"
                    rows={3}
                    value={timesInput}
                    onChange={(event) => setTimesInput(event.target.value)}
                    placeholder="7:15am, 9:15am, 11am"
                  />
                </div>
                <div className="row g-3">
                  <div className="col-12 col-md-6">
                    <label className="form-label">Max matches per team per day</label>
                    <input
                      type="number"
                      min="1"
                      className="form-control"
                      value={maxMatchesPerBlock}
                      onChange={(event) => setMaxMatchesPerBlock(event.target.value)}
                    />
                  </div>
                  <div className="col-12 col-md-6">
                    <label className="form-label">Protected time slots</label>
                    <input
                      type="text"
                      className="form-control"
                      value={protectedTimesInput}
                      onChange={(event) => setProtectedTimesInput(event.target.value)}
                      placeholder="7:15am"
                    />
                  </div>
                </div>
                <button className="btn btn-primary" type="submit" disabled={isScheduling}>
                  {isScheduling ? 'Scheduling...' : 'Generate schedule'}
                </button>
              </div>
            </form>
          </div>
        )}
        <div className={`col-12 ${canManageSchedule ? 'col-lg-7' : ''}`}>
          <div className="card shadow-sm border-0 h-100">
            <div className="card-body d-flex flex-column gap-3">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                <div>
                  <h2 className="h5 mb-1">
                    {isScorerOnly ? 'Assigned matches' : 'Generated schedule'}
                  </h2>
                  <p className="text-muted mb-0">
                    {scheduleSummary || 'No schedule generated yet.'}
                    {scheduleSavedLabel && <span className="ms-2">• {scheduleSavedLabel}</span>}
                  </p>
                </div>
                {canManageSchedule && scheduleOutput.length > 0 && (
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm"
                    onClick={handleSaveSchedule}
                    disabled={isSavingSchedule}
                  >
                    {isSavingSchedule ? 'Saving...' : 'Save schedule'}
                  </button>
                )}
              </div>

              {visibleSchedule.length === 0 && (
                <div className="alert alert-light mb-0">
                  {isScorerOnly
                    ? 'No matches have been assigned to you yet.'
                    : 'Run the generator to see matches here.'}
                </div>
              )}

              {visibleSchedule.length > 0 && (
                <div className="table-responsive">
                  <table className="table table-striped align-middle">
                    <thead>
                      <tr>
                        <th scope="col">Day</th>
                        <th scope="col">Date</th>
                        <th scope="col">Time</th>
                        <th scope="col">Stage</th>
                        <th scope="col">Team 1</th>
                        <th scope="col">Team 2</th>
                        <th scope="col">Match</th>
                        <th scope="col">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleSchedule.map((match, index) => {
                        const matchId = normalizeId(match?.id || match?._id || match?.matchId);
                        const isMatchFinished =
                          Boolean(match?.score?.matchComplete) ||
                          (Number(match?.score?.currentInnings) >= 2 &&
                            (match?.score?.status === 'completed' || match?.score?.status === 'all_out'));
                        const startResumeLabel = getStartResumeLabel(match);
                        const startResumePath = getMatchStartResumePath({
                          match: { ...match, id: matchId },
                          matchBasePath,
                          tossPath: individualTournamentId
                            ? buildMatchManagerMatchTossPath({ ...matchManagerScopeParams, matchId })
                            : '',
                        });
                        const matchRowKey =
                          match._id
                          || match.id
                          || `${match.matchNumber || 'match'}-${match.day || 'day'}-${match.time || 'time'}-${index}`;
                        return (
                          <tr key={matchRowKey}>
                            <td>Day {match.day}</td>
                            <td>{match.dayLabel}</td>
                            <td>{match.time}</td>
                            <td>{match.stageLabel || 'League'}</td>
                            <td>{match.team1}</td>
                            <td>{match.team2}</td>
                            <td>Match {match.matchNumber}</td>
                            <td>
                              {matchId && matchBasePath ? (
                                <div className="d-flex flex-wrap gap-2">
                                  {canConfigureMatch && (
                                    <Link
                                      className="btn btn-sm btn-outline-secondary"
                                      to={`${matchBasePath}/${matchId}/configure`}
                                    >
                                      Configure match
                                    </Link>
                                  )}
                                  {!isMatchFinished ? (
                                    startResumePath ? (
                                      <Link className="btn btn-sm btn-outline-primary" to={startResumePath}>
                                        {startResumeLabel}
                                      </Link>
                                    ) : (
                                      <button type="button" className="btn btn-sm btn-outline-primary" disabled>
                                        {startResumeLabel}
                                      </button>
                                    )
                                  ) : null}
                                  <Link
                                    className="btn btn-sm btn-outline-success"
                                    to={`${matchBasePath}/${matchId}/scoreboard`}
                                  >
                                    View scoreboard
                                  </Link>
                                </div>
                              ) : (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-secondary"
                                  disabled
                                >
                                  {isScorerOnly ? 'Scoring desk' : 'Configure match'}
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {scheduleOutput.length > 0 && canManageSchedule && (
                <p className="text-muted small mb-0">
                  {!scheduleOutput.some((match) => match?.id || match?._id || match?.matchId) &&
                    'Save the schedule to enable match configuration.'}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MatchManagementPage;
